/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  ModalHeader,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon } from '@tonic-one/react-icons';
import { Trans, useTranslation } from 'react-i18next';
import { AppForSa, DeProvisionStatus } from '@Types/administrator';
import { CAS_DOC_LINK, getCasOLHLink } from '../../../../utils/casDocLink';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { AdministrationApi, ProvisionApi } from '@Apis';

const SERVICE_WITH_QUARANTINE = [
  AppForSa.Exchange,
  AppForSa.Teams,
  // AppForSa.TeamsChat,  没有quarantine
  AppForSa.SharePoint,
  AppForSa.OneDrive,
  AppForSa.Box,
  AppForSa.Dropbox,
  AppForSa.GoogleDrive,
  AppForSa.SalesforceSandbox,
  AppForSa.SalesforceProduction,
];

const DEPROVISION_BLOCK_APP = [AppForSa.Gmail, AppForSa.Exchange];

export default function DeprovisionModal({
  orgId,
  app,
  onClose,
  refreshData,
}: {
  orgId: string;
  app: AppForSa;
  onClose: () => void;
  refreshData: () => void;
}) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['common', 'administration', 'errors']);
  const [v1Block, setV1Block] = useState<boolean | null>(
    DEPROVISION_BLOCK_APP.includes(app) ? null : false
  );

  const onClick = () => {
    ProvisionApi.apiDeprovision(orgId, app)
      .then((res) => {
        if (res.data?.status === DeProvisionStatus.Failure) {
          const reason = res.data?.errorCode;
          console.error(`app: ${app} do deprovision failed, reason: ${reason}`);
          window.showNotification({
            severity: 'error',
            titleMsg: t([
              `errors:errors.DEPROVISION;${reason}`,
              'errors:errors.deprovisionCommon',
            ]),
          });
          onClose();
        } else {
          onClose();
          refreshData();
        }
      })
      .catch((e) => {
        console.error(
          `app: ${app} do deprovision failed, reason: ${JSON.stringify(e)}`
        );
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.apiException.internalError'),
        });
      });
  };

  const DeprovisionDesc =
    app === AppForSa.GmailInline ? (
      <Flex direction="column">
        <Text
          dangerouslySetInnerHTML={{
            __html: `${t('administration.sa.deprovision.gmailInline.step1')}
            ${t('administration.sa.deprovision.gmailInline.descStep1')}`,
          }}
        />
        <Text
          dangerouslySetInnerHTML={{
            __html: `${t('administration.sa.deprovision.gmailInline.step2')}
            ${t('administration.sa.deprovision.gmailInline.descStep2')}`,
          }}
        />
      </Flex>
    ) : (
      <Text>{t(`administration.sa.deprovision.${app}.desc`)}</Text>
    );

  const showQuarantineNote = SERVICE_WITH_QUARANTINE.includes(app);

  useEffect(() => {
    // check is block by xdrAddOnStatus
    if (DEPROVISION_BLOCK_APP.includes(app)) {
      AdministrationApi.apiGetXDRAddOnStatus(
        orgId,
        app as AppForSa.Gmail | AppForSa.Exchange
      ).then((res) => {
        const xdrAddOnStatus = res.data;
        if (xdrAddOnStatus) {
          console.warn(
            `app: ${app} do deprovision been blocked, xdrAddOnStatus: ${xdrAddOnStatus}`
          );
        }
        setV1Block(xdrAddOnStatus);
      });
    }
  }, []);

  if (v1Block === null) {
    return <></>;
  }

  if (v1Block) {
    return <DeprovisionBlockModal app={app} onClose={onClose} />;
  }

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={true}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`administration.sa.deprovision.${app}.title`)}
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" gap="12px">
            {DeprovisionDesc}
            <Flex align="center" gap="8px">
              <Icon as={InfoOIcon} color={colorStyle.color.info} />
              <Text fontSize="semibold">{t('common.label.note')}:</Text>
            </Flex>
            <ul style={{ padding: '0 20px', margin: 0 }}>
              {showQuarantineNote && (
                <li>
                  <Text>{t('administration.sa.deprovision.common.note')}</Text>
                </li>
              )}
              <li>
                <Text>
                  {t('administration.sa.deprovision.common.againNote')}
                </Text>
              </li>
            </ul>
            <Text>
              <Trans
                i18nKey="administration.sa.deprovision.common.contact"
                ns="administration"
              >
                placeholders
                <Link
                  href={
                    i18next.language === 'ja'
                      ? 'https://success.trendmicro.com/dcx/s/contactus?language=ja'
                      : getCasOLHLink(
                          CAS_DOC_LINK.deprovision.contact,
                          i18next.language
                        )
                  }
                  target="_blank"
                >
                  placeholder
                </Link>
                placeholder
              </Trans>
            </Text>
            <Space height="40px" />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClick} variant="primary">
            {t('common.action.label.remove')}
          </Button>
          <Space width="2x" />
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const DeprovisionBlockModal = ({
  app,
  onClose,
}: {
  app: AppForSa;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['common', 'administration']);

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={true}
      onClose={onClose}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`administration.sa.deprovision.${app}.title`)}
        </ModalHeader>
        <ModalBody>
          <Text pt="12px">
            {t('administration.sa.deprovision.common.v1Block')}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>{t('common.button.ok')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
